/**
 * Days and times on screen (design 12). A DATE arrives as the text 'YYYY-MM-DD' and is shown as that day, never
 * through `new Date('YYYY-MM-DD')`, which is UTC midnight and so the day before west of Greenwich.
 *
 * PROVENANCE: the formatDay / parseDay rule of ANTON's src/lib/dates.ts @ 3cf5c9f3.
 */

import { localeOf, type Language } from '../i18n/core'

/** 'YYYY-MM-DD' to its parts, or null. */
export function parseDay(day: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null
}

/** A day as the reader's language writes it: "3 Oct 2026", "3 okt. 2026". */
export function formatDay(day: string, language: Language): string {
  const p = parseDay(day)
  if (!p) return day
  return new Intl.DateTimeFormat(localeOf(language), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(p.y, p.m - 1, p.d)),
  )
}

/** A day of the year, 'MM-DD', as the reader's language writes it: "31 Dec", "31 dec.". */
export function formatMonthDay(monthDay: string, language: Language): string {
  const m = /^(\d{2})-(\d{2})$/.exec(monthDay)
  if (!m) return monthDay
  return new Intl.DateTimeFormat(localeOf(language), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2001, Number(m[1]) - 1, Number(m[2]))))
}

/** An instant (an ISO time) as a day in Stockholm, in the reader's language. */
export function formatInstantDay(iso: string, language: Language): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return iso
  return new Intl.DateTimeFormat(localeOf(language), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Stockholm' }).format(t)
}

/** An instant with its time of day, in Stockholm. */
export function formatInstant(iso: string, language: Language): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return iso
  return new Intl.DateTimeFormat(localeOf(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Stockholm',
  }).format(t)
}

export function formatUsd(n: number | null | undefined): string {
  const v = Number(n ?? 0)
  return `US$${v.toFixed(v > 0 && v < 0.01 ? 4 : 2)}`
}

/** Today in Stockholm, the firm's time zone, as 'YYYY-MM-DD' (sv-SE formats a date that way). */
export function todayDay(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Stockholm' }).format(now)
}

/** A weekday and day, for a home's heading: "Friday 3 Oct 2026", "fredag 3 okt. 2026". */
export function formatLongDay(day: string, language: Language): string {
  const p = parseDay(day)
  if (!p) return day
  return new Intl.DateTimeFormat(localeOf(language), { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(p.y, p.m - 1, p.d)),
  )
}
